import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SET_CHART_SYMBOL } from '../graphql/mutations/tokens';

const ChartSymbolModal = ({ isOpen, onClose, token }) => {
  const [chartSymbol, setChartSymbol] = useState(token?.chartSymbol || '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [setTokenChartSymbol] = useMutation(SET_CHART_SYMBOL, {
    onCompleted: () => {
      handleToast('Chart symbol updated', 'success');
      onClose();
    },
    onError: (error) => {
      handleToast(`Error updating chart symbol: ${error.message}`, 'error');
    },
  });

  const handleToast = (message, type) => {
    // Create a toast message div that will automatically disappear
    const toast = document.createElement('div');
    toast.className = `fixed top-4 right-4 p-4 rounded-md shadow-md z-50 ${
      type === 'success' 
        ? 'bg-green-100 text-green-800 border border-green-200' 
        : 'bg-red-100 text-red-800 border border-red-200'
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    // Remove the toast after 3 seconds
    setTimeout(() => {
      toast.classList.add('opacity-0', 'transition-opacity', 'duration-500');
      setTimeout(() => document.body.removeChild(toast), 500);
    }, 3000);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setError('');
    
    setTokenChartSymbol({
      variables: {
        tokenId: token.id,
        chartSymbol: chartSymbol.trim() || null,
      },
    })
    .catch((err) => {
      setError(err.message);
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  };

  const handleClear = () => {
    setChartSymbol('');
  };

  // Don't render anything if not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-semibold mb-2 dark:text-white">Set Custom Chart Symbol</h2>
        <button 
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          onClick={onClose}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Some tokens have chart symbols that are different from their ticker.
          You can set a custom chart symbol to use for chart APIs.
        </p>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Chart Symbol for {token?.ticker}
          </label>
          <input
            value={chartSymbol}
            onChange={(e) => setChartSymbol(e.target.value)}
            placeholder="Enter custom chart symbol"
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>

        <div className="flex justify-end gap-2">
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={handleClear}
          >
            Clear
          </button>
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className={`px-4 py-2 rounded-md text-white flex items-center ${
              isSubmitting 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChartSymbolModal; 